import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSearch, IconX, IconCalendar, IconChevronDown } from '@tabler/icons-react';
import { Input } from '@core-ui/Components/ui/input';
import { Button } from '@core-ui/Components/ui/button';
import { InputSelect, InputSelectTrigger } from '@ui/shared/customSelect';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@core-ui/Components/ui/popover';
import { Calendar } from '@core-ui/Components/ui/calendar';
import { useDashboardStore } from '../store/useDashboardStore';
import type { DatePreset, ContactListType } from '../types/dashboard.types';
import type { DateRange } from 'react-day-picker';
import { useFeatureFlagsStore } from '@store/featureFlags';
import { getCurrentUser } from '@features/auth';

interface FilterBarProps {
  isSuperAdmin: boolean;
  organizations?: Array<{ id: string; name: string }>;
  sdrs?: Array<{ id: string; name: string }>;
  /**
   * Optional server-side search for the pickers. When given, the list holds one
   * bounded page and typing queries the backend. Without them the pickers hold
   * the full list and filter locally, as before.
   */
  onSdrSearch?: (query: string) => void;
  onOrganizationSearch?: (query: string) => void;
  forIntegrationsPage?: boolean;
}

function formatDateShort (date: Date | string | null): string {
  if (date === null || date === '') {
    return '';
  }
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateRange (from: Date | string | null, to: Date | string | null, fallback: string): string {
  const hasFrom = from !== null && from !== '';
  const hasTo = to !== null && to !== '';

  if (!hasFrom && !hasTo) {
    return fallback;
  }
  if (hasFrom && !hasTo) {
    return formatDateShort(from);
  }
  if (hasFrom && hasTo) {
    return `${formatDateShort(from)} - ${formatDateShort(to)}`;
  }
  return fallback;
}

export default function FilterBar ({ isSuperAdmin, organizations = [], sdrs = [], forIntegrationsPage = false, onSdrSearch, onOrganizationSearch }: FilterBarProps): React.JSX.Element {
  const { t } = useTranslation();
  const { filters, setFilters, setDatePreset, setCustomDateRange, clearFilters, clearActiveSessions } = useDashboardStore();
  const [searchValue, setSearchValue] = useState(filters.searchQuery);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const loadFeatureFlags = useFeatureFlagsStore((state) => state.loadFeatureFlags);
  const currentUser = getCurrentUser();
  const featureFlags = useFeatureFlagsStore((s) => s.flags);

  const TENDRIL_NAME = 'Tendril LLC';
  const TENDRIL_ORG = organizations.find(org => org.name === TENDRIL_NAME);
  const TENDRIL_ID = TENDRIL_ORG != null ? TENDRIL_ORG.id : null;

  const DATE_PRESETS: Array<{ value: DatePreset; label: string }> = useMemo(() => [
    { value: 'today', label: t('dashboard.filterBar.today') },
    { value: 'yesterday', label: t('dashboard.filterBar.yesterday') },
    { value: 'last7days', label: t('dashboard.filterBar.last7days') },
    { value: 'last30days', label: t('dashboard.filterBar.last30days') },
    { value: 'thisMonth', label: t('dashboard.filterBar.thisMonth') },
    { value: 'custom', label: t('dashboard.filterBar.customRange') },
  ], [t]);

  const [typeFilter, setTypeFilter] = useState<Array<{ value: string; label: string }>>([
    { value: 'CSV', label: 'CSV' },
  ]);

  const [integrationFilter, setintegrationFilter] = useState<Array<{ value: string; label: string }>>([]);

  useEffect(() => {
    if (currentUser === null) {
      throw new Error('No current user found in session store.');
    }

    void loadFeatureFlags(currentUser.organizationId);
  }, []);

  useEffect(() => {
    const defaultValues: Array<{ value: string; label: string }> =
        forIntegrationsPage ? [] : [
          { value: 'CSV', label: 'CSV' },
        ];

    if (featureFlags.salesforce) {
      defaultValues.push(
        { value: 'SALESFORCE', label: 'Salesforce' },
      );
    }

    if (featureFlags.hubspot) {
      defaultValues.push(
        { value: 'HUBSPOT', label: 'Hubspot' },
      );
    }

    if (featureFlags.outreach) {
      defaultValues.push(
        { value: 'OUTREACH', label: 'Outreach' },
      );
    }

    if (forIntegrationsPage) {
      setintegrationFilter(defaultValues);
    } else {
      setTypeFilter(defaultValues);
    }
  }, []);

  const handleSearchSubmit = useCallback(() => {
    setFilters({ searchQuery: searchValue });
  }, [searchValue, setFilters]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  }, [handleSearchSubmit]);

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    setFilters({ searchQuery: '' });
  }, [setFilters]);

  const handleTypeChange = useCallback((value: string) => {
    setFilters({ listTypes: value === 'all' || value === '' ? [] : [value as ContactListType] });
  }, [setFilters]);

  const handleSdrChange = useCallback((value: string) => {
    setFilters({ sdrIds: value === 'all' || value === '' ? [] : [value] });
  }, [setFilters]);

  const handleOrgChange = useCallback((value: string) => {
    setFilters({ orgIds: value === 'all' || value === '' ? [] : [value] });
  }, [setFilters]);

  // Convert SDRs to options format for InputSelect
  const sdrOptions = useMemo(() =>
    sdrs.map((sdr) => ({
      value: sdr.id,
      label: sdr.name,
    })),
  [sdrs]
  );

  // Convert organizations to options format for InputSelect
  const orgOptions = useMemo(() =>
    organizations.map((org) => ({
      value: org.id,
      label: org.name,
    })),
  [organizations]
  );

  const handlePresetSelect = useCallback((preset: DatePreset) => {
    if (preset === 'custom') {
      setShowCalendar(true);
    } else {
      setDatePreset(preset);
      setDatePickerOpen(false);
      setShowCalendar(false);
    }
  }, [setDatePreset]);

  const handleDateRangeSelect = useCallback((range: DateRange | undefined) => {
    if (range?.from !== undefined) {
      let endDate: Date | null = null;

      if (range.to !== undefined && range.to !== null) {
        const adjustToEndDay = new Date(range.to);
        adjustToEndDay.setHours(23, 59, 59);
        endDate = adjustToEndDay;
      }
      setCustomDateRange(range.from, endDate ?? null);
    }
  }, [setCustomDateRange]);

  const handleApplyCustomRange = useCallback(() => {
    setDatePickerOpen(false);
    setShowCalendar(false);
  }, []);

  const handleCancelCustomRange = useCallback(() => {
    setShowCalendar(false);
  }, []);

  // Reset showCalendar when opening the popover
  const handleDatePickerOpenChange = useCallback((open: boolean) => {
    setDatePickerOpen(open);
    if (!open) {
      setShowCalendar(false);
    } else if (filters.dateRange.preset === 'custom') {
      // If already on custom, show calendar directly
      setShowCalendar(true);
    }
  }, [filters.dateRange.preset]);

  // Convert stored dates to Date objects for the calendar
  const selectedRange: DateRange | undefined = filters.dateRange.from !== null
    ? {
      from: filters.dateRange.from instanceof Date
        ? filters.dateRange.from
        : new Date(filters.dateRange.from),
      to: filters.dateRange.to !== null
        ? (filters.dateRange.to instanceof Date
          ? filters.dateRange.to
          : new Date(filters.dateRange.to))
        : undefined,
    }
    : undefined;

  const isCustom = filters.dateRange.preset === 'custom';
  const currentPresetLabel = isCustom
    ? formatDateRange(filters.dateRange.from, filters.dateRange.to, t('dashboard.filterBar.selectDates'))
    : DATE_PRESETS.find(p => p.value === filters.dateRange.preset)?.label ?? t('dashboard.filterBar.selectPeriod');

  const hasTendrilFilter =
      isSuperAdmin &&
      TENDRIL_ID != null &&
      filters.orgIds[0] !== TENDRIL_ID;

  const isActiveOnly = filters.statuses.length > 0;

  const hasActiveFilters = filters.searchQuery !== '' ||
    filters.listTypes.length > 0 ||
    (filters.sdrIds?.length ?? 0) > 0 ||
    filters.dateRange.preset !== 'last7days' ||
      isActiveOnly ||
      hasTendrilFilter;

  /**
   * Back to the date-driven list, keeping every other filter in place.
   *
   * Goes through the store so the date range the drill-down replaced is
   * restored, rather than resetting to a default the user never picked.
   */
  const handleClearStatusFilter = (): void => {
    clearActiveSessions();
  };

  const handleClearFilters = (): void => {
    clearFilters();
    setSearchValue('');
    if (isSuperAdmin) {
      if (TENDRIL_ID != null) {
        handleOrgChange(TENDRIL_ID);
      }
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-card rounded-xl border border-border">
      {(!forIntegrationsPage) && (
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-qa-id="search-bar"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchSubmit}
            placeholder={t('dashboard.filterBar.searchPlaceholder')}
            className="pl-10 pr-10 h-9 bg-background border-border truncate"
          />
          {searchValue !== '' && (
            <button
              aria-label="Clear search"
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <IconX className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
      )}

      <Popover open={datePickerOpen} onOpenChange={handleDatePickerOpenChange}>
        <PopoverTrigger asChild>
          <button
            data-qa-id="calendar-filter"
            type="button"
            className={`flex items-center justify-between gap-2 h-9 px-2 rounded-md border text-sm transition-colors ${
              isCustom
                ? 'bg-primary/10 text-primary border-primary/30'
                : 'bg-background text-foreground border-border hover:bg-muted/50'
            }`}
            style={{ minWidth: '140px' }}
          >
            <div className="flex items-center gap-2 px-1">
              <IconCalendar className="h-4 w-4" />
              <span>{currentPresetLabel}</span>
            </div>
            <IconChevronDown className="mx-2 h-4 w-4 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
          {!showCalendar ? (
            <div className="p-1 min-w-[160px]">
              {DATE_PRESETS.map((preset) => (
                <button
                  data-qa-id={preset.label}
                  key={preset.value}
                  type="button"
                  onClick={() => {
                    handlePresetSelect(preset.value); 
                  }}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                    filters.dateRange.preset === preset.value
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-muted'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          ) : (
            <>
              <Calendar
                mode="range"
                selected={selectedRange}
                onSelect={handleDateRangeSelect}
                numberOfMonths={2}
                defaultMonth={selectedRange?.from ?? new Date()}
                showOutsideDays={false}
                classNames={{
                  button_previous: 'z-20 relative',
                  button_next: 'z-20 relative',
                  caption_label: 'z-0',
                }}
              />
              <div className="flex justify-between gap-2 p-3 border-t border-border">
                <Button
                  data-qa-id="back-button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelCustomRange}
                >
                  {t('dashboard.filterBar.back')}
                </Button>
                <Button
                  data-qa-id="apply-button"
                  size="sm"
                  onClick={handleApplyCustomRange}
                  disabled={filters.dateRange.from === null}
                >
                  {t('dashboard.filterBar.apply')}
                </Button>
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>

      <InputSelect
        options={forIntegrationsPage ? integrationFilter : typeFilter}
        value={filters.listTypes[0] ?? ''}
        onValueChange={(value) => {
          handleTypeChange(value !== '' ? value : 'all');
        }}
        placeholder={forIntegrationsPage ? t('dashboard.filterBar.allIntegrations') : t('dashboard.filterBar.allTypes')}
        clearable
      >
        {(props) => (
          <InputSelectTrigger
            {...props}
            className="w-[160px] h-9"
          />
        )}
      </InputSelect>

      {sdrs.length > 0 && (
        <InputSelect
          onSearchChange={onSdrSearch}
          options={sdrOptions}
          value={filters.sdrIds?.[0] ?? ''}
          onValueChange={(value) => {
            handleSdrChange(value !== '' ? value : 'all');
          }}
          placeholder={t('dashboard.filterBar.allUsers')}
          clearable
        >
          {(props) => (
            <InputSelectTrigger
              {...props}
              className="w-[200px] h-9"
            />
          )}
        </InputSelect>
      )}

      {isSuperAdmin && organizations.length > 0 && (
        <InputSelect
          onSearchChange={onOrganizationSearch}
          options={orgOptions}
          value={filters.orgIds?.[0] ?? ''}
          onValueChange={(value) => {
            handleOrgChange(value !== '' ? value : 'all');
          }}
          placeholder={t('dashboard.filterBar.allOrganizations')}
        >
          {(props) => (
            <InputSelectTrigger
              {...props}
              className="w-[250px] h-9 text-sm"
            />
          )}
        </InputSelect>
      )}

      {/* Says out loud that the list is not obeying the date picker.
          Arriving from the Active Sessions tile clears the range on purpose,
          and without this the list just looks like it ignored the filter. */}
      {isActiveOnly && (
        <button
          type="button"
          onClick={handleClearStatusFilter}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 text-xs font-medium hover:bg-emerald-500/20"
          title={t('dashboard.filterBar.activeOnlyHelp', 'Showing every session still running or paused, whatever the date. Click to clear.')}
        >
          {t('dashboard.filterBar.activeOnly', 'Active sessions only')}
          <IconX size={14} />
        </button>
      )}

      <Button
        data-qa-id="clear-all-button"
        variant="ghost"
        size="sm"
        disabled={!hasActiveFilters}
        onClick={handleClearFilters}
        className="text-red-500 hover:text-red-600 h-9 disabled:opacity-50"
      >
        {t('dashboard.filterBar.clearAll')}
      </Button>
    </div>
  );
}